import {
  AssessmentSource,
  ASSESSMENTS_NATIVE_BRIDGE_NAME,
  AssessmentsNativeInboundEventType,
  AssessmentsNativeOutboundEventType,
} from '../constants';

// Payload carried by a SET_AUTH inbound event. Mirrors setAssessmentsDetails's action payload
// so it can be dispatched as-is.
export interface AssessmentsNativeAuthPayload {
  source: AssessmentSource;
  accessToken: string | null;
  assessmentsId: string | null;
  activityId: string | null;
  clientPatientId: string | null;
  firstName: string | null;
  // When accessToken expires, as epoch seconds in a string (same shape as the token exchange
  // response). Drives when the page asks native for a fresh token - without it no refresh is asked.
  expiresAt?: string | null;
}

// Payload carried by an UPDATE_TOKEN inbound event - just the refreshed token and its expiry; the
// identifiers from SET_AUTH are left untouched.
export interface AssessmentsNativeTokenPayload {
  accessToken: string;
  // Epoch seconds in a string, like SET_AUTH's. The next REFRESH_TOKEN is scheduled from it.
  expiresAt?: string;
}

// Inbound: events the native app sends INTO the page via window.postAssessmentsNativeEvent,
// queued in window.__assessmentsBridgeQueue and drained by utils/nativeBridgeQueue.ts.
// A discriminated union keyed on `type` - add a new variant here (and a matching case in
// useAssessmentsNativeBridge) rather than assuming every event is auth.
export type AssessmentsNativeInboundEvent =
  | {
      type: AssessmentsNativeInboundEventType.SET_AUTH;
      payload: AssessmentsNativeAuthPayload;
      eventId?: string;
    }
  | {
      type: AssessmentsNativeInboundEventType.ASSESSMENT_CLOSE_REQUESTED;
      payload?: Record<string, unknown>;
      eventId?: string;
    }
  | {
      type: AssessmentsNativeInboundEventType.UPDATE_TOKEN;
      payload: AssessmentsNativeTokenPayload;
      eventId?: string;
    };

// Outbound: events the page sends OUT to the native app via notifyNativeApp.
export interface AssessmentsNativeOutboundEvent {
  type: AssessmentsNativeOutboundEventType;
  tenantId?: string | null;
  surveyEventId?: string | null;
  // Set when type is EVENT_CONSUMED - which inbound event this is acknowledging.
  consumedEventType?: AssessmentsNativeInboundEventType;
  // Echoes the inbound event's optional identifier when type is EVENT_CONSUMED.
  eventId?: string;
}

// What window.postAssessmentsNativeEvent's promise resolves with once the page has pulled the
// event off the queue - the same content as the EVENT_CONSUMED message sent to native.
export interface AssessmentsNativeInboundReceipt {
  type: AssessmentsNativeOutboundEventType.EVENT_CONSUMED;
  consumedEventType: AssessmentsNativeInboundEventType;
  eventId?: string;
}

// One entry in window.__assessmentsBridgeQueue: the event plus the resolver for the promise that
// postAssessmentsNativeEvent handed back to native.
export interface AssessmentsNativeQueueEntry {
  event: AssessmentsNativeInboundEvent;
  resolve: (receipt: AssessmentsNativeInboundReceipt) => void;
}

// Reply native resolves each outbound event with, keyed by the event's `type`. Add a field/shape
// here when native starts replying to that event; `undefined` means no reply is defined yet.
export interface AssessmentsNativeReplyMap {
  [AssessmentsNativeOutboundEventType.ASSESSMENT_SUBMITTED]: undefined;
  [AssessmentsNativeOutboundEventType.ASSESSMENT_SAVED_AND_CLOSED]: undefined;
  // Native acknowledges immediately; the refreshed token arrives later as UPDATE_TOKEN.
  [AssessmentsNativeOutboundEventType.REFRESH_TOKEN]: { status: 'waiting' };
  [AssessmentsNativeOutboundEventType.EVENT_CONSUMED]: undefined;
}

declare global {
  interface Window {
    webkit?: {
      // A WKScriptMessageHandlerWithReply handler returns a Promise of the native reply; a plain
      // WKScriptMessageHandler returns undefined.
      messageHandlers?: Record<string, { postMessage: (message: string) => unknown }>;
    };
    // Android's addJavascriptInterface returns synchronously (void or a string).
    AssessmentsBridge?: { postMessage: (message: string) => unknown };
    // Queue populated by the bootstrap script (public/scripts/assessmentsNativeBridge.js).
    // Drained on a plain interval by utils/nativeBridgeQueue.ts, not on every render.
    __assessmentsBridgeQueue?: AssessmentsNativeQueueEntry[];
    // Single generic entry point native calls for every inbound event - see the bootstrap script.
    // Resolves with a receipt once the page has consumed the event.
    postAssessmentsNativeEvent?: (
      event: AssessmentsNativeInboundEvent,
    ) => Promise<AssessmentsNativeInboundReceipt>;
  }
}

// The native app registers a handler under ASSESSMENTS_NATIVE_BRIDGE_NAME on its side to receive
// outbound events (see notifyNativeApp below) - iOS as a WKScriptMessageHandlerWithReply, Android via
// addJavascriptInterface, both under the same name. Its presence is also our signal that we're
// running inside the mobile app's webview and should wait for it to deliver auth via a SET_AUTH
// event, rather than immediately falling back to the manual verify-login flow.
export const isInNativeAssessmentsWebview = (): boolean =>
  Boolean(
    window.webkit?.messageHandlers?.[ASSESSMENTS_NATIVE_BRIDGE_NAME] ||
      window[ASSESSMENTS_NATIVE_BRIDGE_NAME as 'AssessmentsBridge'],
  );

// Sends an event out to the native app. No-ops outside the native webview (e.g. a normal browser
// tab).
// Resolves with the native reply when there is one (iOS WKScriptMessageHandlerWithReply, or a
// value returned synchronously by Android's interface), otherwise undefined. The reply's shape
// depends on the event type - see AssessmentsNativeReplyMap. Never rejects, so callers that don't
// need a reply can ignore the result.
export const notifyNativeApp = async <T extends AssessmentsNativeOutboundEventType>(
  event: AssessmentsNativeOutboundEvent & { type: T },
): Promise<AssessmentsNativeReplyMap[T] | undefined> => {
  const message = JSON.stringify(event);
  try {
    const iosReply =
      window.webkit?.messageHandlers?.[ASSESSMENTS_NATIVE_BRIDGE_NAME]?.postMessage(message);
    const androidReply =
      window[ASSESSMENTS_NATIVE_BRIDGE_NAME as 'AssessmentsBridge']?.postMessage(message);
    const reply = await (iosReply ?? androidReply);
    // Android's interface can only return a string, so its replies are JSON text; iOS already
    // hands back a decoded object.
    return (typeof reply === 'string' && reply !== '' ? JSON.parse(reply) : reply) as
      | AssessmentsNativeReplyMap[T]
      | undefined;
  } catch {
    // Native rejected or never replied (e.g. handler deallocated) - the event itself was still sent.
    return undefined;
  }
};
